import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import { u3125CanModifyInvoice, type U3125InvoiceFormRow, type U3125InvoiceLineGridRow } from "../../../../U3125Service";
import { NS, U3125ROLES, u3125FormatCurrency } from "../../../../U3125";
import { useU3125InvoiceLineHandler, type U3125FactureNestedParentHandlers } from "./U3125InvoiceLineHandlers";
import U3125InvoiceLineEditDlg from "./U3125InvoiceLineEditDlg";
import U3125InvoiceLineDeleteDlg from "./U3125InvoiceLineDeleteDlg";

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "position",
      label: t("colLinePosition", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[6%]",
      type: "number",
      align: "right",
    },
    {
      name: "kind",
      label: t("lblLineKind", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[12%]",
      type: "string",
      align: "left",
    },
    {
      name: "description",
      label: t("lblLineDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[28%]",
      type: "string",
      align: "left",
    },
    {
      name: "quantity",
      label: t("lblLineQuantity", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[8%]",
      type: "number",
      align: "right",
    },
    {
      name: "unitPrice",
      label: t("lblLineUnitPrice", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "amount",
      label: t("lblLineAmount", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[10%]",
      type: "number",
      align: "right",
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "taxable",
      label: t("lblLineTaxable", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[8%]",
      type: "string",
      align: "center",
      display: (_c: U2000TableColumn, value: unknown) => (value ? t("lblYes", { ns: NS }) : t("lblNo", { ns: NS })),
    },
  ];
}

interface U3125InvoiceLinesTabBodyProps {
  parentFormHandlers: U3125FactureNestedParentHandlers & { getData: () => U3125InvoiceFormRow | null };
}

const U3125InvoiceLinesTabBody: React.FC<U3125InvoiceLinesTabBodyProps> = ({ parentFormHandlers }) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const gridStateHandlers = useU2000GridStateHandlers<U3125InvoiceLineGridRow>();

  const invoiceData = parentFormHandlers.getData?.() ?? null;
  const lines = invoiceData?.lines;

  const [formState, formStateDispatch] = React.useReducer(u2000FormState, u2000IntFormState);
  const businessHandlers: U2000FormSBusinessHandlers<U3125InvoiceLineGridRow> = useMemo(
    () => ({
      newRecord: () => ({ id: "", position: 0, kind: "LABOR", description: "", quantity: null, unitPrice: null, amount: 0, taxable: true, wipAmount: null, wipLineIds: [], appliedFromInvoiceId: null }),
      validateForm: (data) => {
        const result: Record<string, string> = {};
        if (!data?.description?.trim()) result.description = t("errLineDescriptionRequired", { ns: NS });
        return result;
      },
    }),
    [t],
  );
  const lineFormStateHandlers = useU2000FormStateHandlers<U3125InvoiceLineGridRow>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const handler = useU3125InvoiceLineHandler({
    gridStateHandlers,
    t,
    formStateHandlers: lineFormStateHandlers,
    parentFormHandlers,
  });

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const raw = Array.isArray(lines) ? lines : [];
    const list = raw.map((l) => ({ ...l, id: l.id?.trim() || generateTemporaryId() }) as U3125InvoiceLineGridRow);
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [t, lines]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) return;
    const inv = parentFormHandlers.getData();
    if (!inv) return;
    const nextLines = gridStateHandlers.getData().map((row, idx) => ({ ...row, position: idx }));
    parentFormHandlers.setData({ ...inv, lines: nextLines });
  }, [handler.dataChangedEvent, parentFormHandlers, gridStateHandlers]);

  const isEditMode =
    parentFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT && u3125CanModifyInvoice(parentFormHandlers.getData()?.status);
  const disabledModify = !isEditMode || handler.gridStateHandlers.getSelectedRows().length !== 1;
  const disabledDelete = !isEditMode || handler.gridStateHandlers.getSelectedRows().length === 0;

  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: handler.onAdd,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: !isEditMode,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: handler.onModify,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: disabledModify,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handler.onDelete,
        hasRole: hasRole(U3125ROLES.EDIT),
        disabled: disabledDelete,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, handler, isEditMode, disabledModify, disabledDelete, hasRole],
  );

  return (
    <>
      <U3125InvoiceLineEditDlg u3125InvoiceLineHandler={handler} parentFormHandlers={parentFormHandlers} t={t} NS={NS} />
      <U3125InvoiceLineDeleteDlg u3125InvoiceLineHandler={handler} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={(row) => handler.onRowClick(row as U3125InvoiceLineGridRow)}
        allowRowSelection={isEditMode}
        label={t("lblInvoiceLinesGrid", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
};

export default U3125InvoiceLinesTabBody;
